import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SidebarLayout from "./SidebarLayout";
import "./shopStyles.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const BookingSettings = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [businessId, setBusinessId] = useState(null);

  const [bookingSettings, setBookingSettings] = useState({
    advanceBookingDays: 30,
    cancellationHours: 24,
    depositType: "none",
    depositAmount: 0,
    noShowFee: 0,
    bufferTimeMinutes: 0,
    allowSameDayBooking: true,
  });

  const [productSettings, setProductSettings] = useState({
    allowProfessionalProductSales: false,
    requireProductApproval: true,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      let busId = null;

      try {
        const staffRes = await axios.get(`${API_BASE_URL}/api/staff/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const staffData = staffRes.data.staff || staffRes.data;

        if (staffData) {
          busId =
            typeof staffData.businessId === "object"
              ? staffData.businessId?._id
              : staffData.businessId;
        }
      } catch (error) {
      }

      if (!busId) {
        const shopsRes = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const shops = Array.isArray(shopsRes.data)
          ? shopsRes.data
          : shopsRes.data.shops || [];

        if (shops.length > 0) {
          busId = shops[0].businessId?._id || shops[0].businessId;
        }
      }

      if (!busId) {
        showAlert("No business associated with your account", "error");
        setLoading(false);
        return;
      }

      setBusinessId(busId);

      const businessRes = await axios.get(
        `${API_BASE_URL}/api/businesses/${busId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (businessRes.data.bookingSettings) {
        setBookingSettings(businessRes.data.bookingSettings);
      }

      if (businessRes.data.productSettings) {
        setProductSettings(businessRes.data.productSettings);
      }

      setLoading(false);
    } catch (err) {
      showAlert("Failed to load settings", "error");
      setLoading(false);
    }
  };

  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 5000);
  };

  const handleSaveBookingSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("ogestyle_token");

      await axios.put(
        `${API_BASE_URL}/api/businesses/${businessId}/booking-settings`,
        { bookingSettings },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      showAlert("Booking settings updated successfully", "success");
      setSaving(false);
    } catch (err) {
      showAlert(
        err.response?.data?.message || "Failed to save settings",
        "error",
      );
      setSaving(false);
    }
  };

  const handleSaveProductSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("ogestyle_token");

      await axios.put(
        `${API_BASE_URL}/api/businesses/${businessId}/product-settings`,
        { productSettings },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      showAlert("Product settings updated successfully", "success");
      setSaving(false);
    } catch (err) {
      showAlert(
        err.response?.data?.message || "Failed to save product settings",
        "error",
      );
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="bookings-container settings-page">
          <div className="loading-state">Loading settings...</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="bookings-container settings-page">
        <button
          onClick={() => navigate("/dashboard/settings")}
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px 16px",
            fontSize: "14px",
            color: "#374151",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
            transition: "all 0.2s",
            fontWeight: "500",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f9fafb";
            e.currentTarget.style.borderColor = "#d1d5db";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }}
        >
          <span>←</span>
          <span>Back to Settings</span>
        </button>

        {alert.show && (
          <div className={`alert alert-${alert.type}`}>{alert.message}</div>
        )}

        <div className="bookings-header">
          <div>
            <h1
              className="bookings-title"
              style={{ fontSize: isMobile ? "24px" : "28px" }}
            >
              Booking Settings
            </h1>
            <p
              className="bookings-subtitle"
              style={{ fontSize: isMobile ? "14px" : "16px" }}
            >
              Configure how customers can book appointments
            </p>
          </div>
        </div>

        <div className="settings-section">
          <div className="form-group">
            <label>Advance Booking Window (days)</label>
            <input
              type="number"
              className="form-input"
              value={bookingSettings.advanceBookingDays}
              onChange={(e) =>
                setBookingSettings({
                  ...bookingSettings,
                  advanceBookingDays: parseInt(e.target.value),
                })
              }
              min="1"
              max="365"
            />
            <span className="form-hint">
              How far in advance can customers book
            </span>
          </div>

          <div className="form-group">
            <label>Cancellation Policy (hours before)</label>
            <input
              type="number"
              className="form-input"
              value={bookingSettings.cancellationHours}
              onChange={(e) =>
                setBookingSettings({
                  ...bookingSettings,
                  cancellationHours: parseInt(e.target.value),
                })
              }
              min="0"
              max="168"
            />
            <span className="form-hint">
              Minimum notice required for cancellations
            </span>
          </div>

          <div className="form-group">
            <label>Deposit Requirement</label>
            <select
              className="form-input"
              value={bookingSettings.depositType}
              onChange={(e) =>
                setBookingSettings({
                  ...bookingSettings,
                  depositType: e.target.value,
                })
              }
            >
              <option value="none">No Deposit</option>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
          </div>

          {bookingSettings.depositType !== "none" && (
            <div className="form-group">
              <label>
                Deposit Amount{" "}
                {bookingSettings.depositType === "percentage" ? "(%)" : "($)"}
              </label>
              <input
                type="number"
                className="form-input"
                value={bookingSettings.depositAmount}
                onChange={(e) =>
                  setBookingSettings({
                    ...bookingSettings,
                    depositAmount: parseFloat(e.target.value),
                  })
                }
                min="0"
                max={
                  bookingSettings.depositType === "percentage" ? "100" : "10000"
                }
                step={bookingSettings.depositType === "percentage" ? "5" : "5"}
              />
            </div>
          )}

          <div className="form-group">
            <label>No-Show Fee ($)</label>
            <input
              type="number"
              className="form-input"
              value={bookingSettings.noShowFee}
              onChange={(e) =>
                setBookingSettings({
                  ...bookingSettings,
                  noShowFee: parseFloat(e.target.value),
                })
              }
              min="0"
              step="5"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={bookingSettings.allowSameDayBooking}
                onChange={(e) =>
                  setBookingSettings({
                    ...bookingSettings,
                    allowSameDayBooking: e.target.checked,
                  })
                }
              />
              <span>Allow same-day bookings</span>
            </label>
          </div>

          <div className="settings-actions" style={{ flexWrap: "wrap" }}>
            <button
              className="btn btn-primary"
              onClick={handleSaveBookingSettings}
              disabled={saving}
              style={{ width: isMobile ? "100%" : "auto" }}
            >
              {saving ? "Saving..." : "Save Booking Settings"}
            </button>
          </div>
        </div>

        {/* Product Sales Settings */}
        <div className="settings-section" style={{ marginTop: "32px" }}>
          <div className="bookings-header" style={{ marginBottom: "24px" }}>
            <div>
              <h2
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "600",
                  color: "#111827",
                  marginBottom: "8px",
                }}
              >
                Product Sales Settings
              </h2>
              <p
                style={{
                  fontSize: isMobile ? "14px" : "16px",
                  color: "#6b7280",
                }}
              >
                Control how professionals can sell products at your business
              </p>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={productSettings.allowProfessionalProductSales}
                onChange={(e) =>
                  setProductSettings({
                    ...productSettings,
                    allowProfessionalProductSales: e.target.checked,
                  })
                }
              />
              <span>
                Allow professionals to add and sell their own products
              </span>
            </label>
            <span className="form-hint">
              When enabled, professionals can list products for sale to
              customers. Product seller payouts are handled by the platform fee rules.
            </span>
          </div>

          {productSettings.allowProfessionalProductSales && (
            <>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={productSettings.requireProductApproval}
                    onChange={(e) =>
                      setProductSettings({
                        ...productSettings,
                        requireProductApproval: e.target.checked,
                      })
                    }
                  />
                  <span>Require owner approval for professional products</span>
                </label>
                <span className="form-hint">
                  When enabled, you must approve products before they appear to
                  customers
                </span>
              </div>
            </>
          )}

          <div className="form-actions">
            <button
              className="btn-primary"
              onClick={handleSaveProductSettings}
              disabled={saving}
              style={{ width: isMobile ? "100%" : "auto" }}
            >
              {saving ? "Saving..." : "Save Product Settings"}
            </button>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default BookingSettings;
